/* TMF `ClarificationCase` → the cockpit's `CaseRecord`.
 *
 * The two vocabularies do not line up, and that is on purpose. The API speaks
 * TMF622 — an action, a category, and whether an existing service is cited. The
 * cockpit speaks in seven order types a person recognises. This file is the only
 * place the translation happens, so a screen never has to know that `NEW` is
 * really `add` + `Provide` + no existing service.
 *
 * Three fields cannot be answered from the case row alone, and each takes an
 * index the transport already has to fetch anyway:
 *
 *   customer  — the case carries a customer id; the name comes from the customer
 *               list the cockpit loads for its own screens.
 *   offering  — the case carries a service-candidate id; the name comes from the
 *               offering list.
 *   order     — a case that has been signed off projects to a TMF622
 *               ProductOrder, whose `externalId` is the reference people quote.
 *
 * All three degrade to something honest when the index has no entry, rather than
 * to a plausible-looking placeholder. A blank offering reads as "not matched",
 * which is a real state; an invented name reads as a match that never happened.
 */

import type { components } from '@/shared/api/generated/openapi';
import { readable } from '@/shared/api/http/map/ids';
import type {
  CaseRecord,
  CaseStateKey,
  OrderTypeKey,
  QueueKey,
} from '@/shared/domain/case';
import type { CaseAttention } from '@/shared/domain/status';
// `orderTypes` is the classification taxonomy itself, and the case table, case
// rail and routing tab all read it. Inverting the existing table keeps the
// mapping aligned with what the screens render. `caseStates` and `desks` are
// there for the same reason, and carry no words at all — the words are
// `domain:caseStates.<Key>`, assembled by `vocabulary.ts` at render.
import { caseStates, desks, orderTypes } from '@/shared/domain/taxonomy';

export type WireCase = components['schemas']['ClarificationCaseModel'];

/** What the mapper needs beyond the case itself. Every field is optional: a
    partially-loaded index produces a partially-named case, never a thrown one. */
export interface CaseContext {
  /** Customer id → display name. */
  readonly customerNames?: ReadonlyMap<string, string>;
  /** Service-candidate id → offering name. */
  readonly offeringNames?: ReadonlyMap<string, string>;
  /** Case id → the `externalId` of the order it projects to. */
  readonly orderRefs?: ReadonlyMap<string, string>;
  /** Case id → the number of open blocking gaps on it. */
  readonly openGapCounts?: ReadonlyMap<string, number>;
  /** The instant "now" is measured from, so a page of rows cannot disagree with
      itself about how old anything is. */
  readonly now?: Date;
}

/* ---------- classification ---------- */

/** The wire's three fields → one of the cockpit's seven order types.
 *
 * Derived from `orderTypes` rather than written out again. The table says
 * `MOVE` is `modify` + `Move` + an existing service; this reads that back. If
 * somebody adds an eighth type, this classifies it without being touched.
 *
 * `NONORDER` is checked first because its wire form is the absence of a
 * classification with a queue that says so, and `UNCLASSIFIED` is the fallback
 * for everything the table does not recognise — which is the honest answer for a
 * case the analysis has not typed yet.
 */
export function classifyOrderType(wire: WireCase): OrderTypeKey {
  if (wire.routingQueue === 'non-order') return 'NONORDER';

  const action = wire.orderAction;
  const category = wire.orderCategory;
  if (!action || !category) return 'UNCLASSIFIED';

  const citesService = Boolean(wire.existingServiceId);
  const entries = Object.entries(orderTypes) as readonly [OrderTypeKey, (typeof orderTypes)[OrderTypeKey]][];
  const exact = entries.find(
    ([, type]) => type.action === action && type.category === category && type.productRef === citesService,
  );
  if (exact) return exact[0];

  // The action and category agree with a known type but the service reference
  // does not. That is a real disagreement — an `add` against `Provide` that
  // names an existing service is either an add-on mislabelled or a provide with
  // a stray reference — and the classification is not asserted.
  return 'UNCLASSIFIED';
}

/* ---------- routing ---------- */

/** The API's queue names → the cockpit's.
 *
 * Four of the five are the same word. The fifth is not: the API calls it
 * `service-exception`, which is what it is — a request that named a service the
 * catalog cannot supply — and the cockpit calls it `catalog`, which is who has
 * to act. Renaming either side would touch a database CHECK or every route in
 * the app, so the two names are reconciled here instead.
 */
const QUEUES: Readonly<Record<string, QueueKey>> = {
  work: 'work',
  macd: 'macd',
  'service-exception': 'catalog',
  'non-order': 'non-order',
  parked: 'parked',
};

/** An unrecognised queue parks the case rather than dropping it.
 *
 * A queue the cockpit has no screen for would otherwise make the case invisible,
 * and an invisible case in a work queue is the failure mode this whole product
 * exists to prevent. Parked is where things go when the system cannot say where
 * they belong, which is exactly the situation. */
export function toQueue(routingQueue: string): QueueKey {
  return QUEUES[routingQueue] ?? 'parked';
}

/* ---------- lifecycle ---------- */

/** The wire's state token → the key the cockpit knows it by, or null.
 *
 * `caseStates` is the set of states this cockpit has a name for, so recognising
 * one is asking whether the token is a key in it. Null is not an error: the
 * state column falls back to the token itself, because a state added to the
 * API's CHECK constraint before this cockpit learns about it has to show its own
 * name — that name is the only string identifying what happened.
 */
function toStateKey(state: string): CaseStateKey | null {
  return Object.hasOwn(caseStates, state) ? (state as CaseStateKey) : null;
}

/* ---------- age ---------- */

/** `createdAt` → the `3d` / `6h` / `2w` shape the table sorts on.
 *
 * `ageHours()` in `shared/domain/selectors.ts` parses exactly `\d+[hdw]`, and
 * anything else it reads as zero — so a friendlier string like "3 days" would
 * silently sort every row to the top. The format is a contract with that parser,
 * not a display choice.
 */
export function toAge(createdAt: string, now: Date): string {
  const opened = Date.parse(createdAt);
  if (Number.isNaN(opened)) return '0h';

  const hours = Math.max(0, Math.floor((now.getTime() - opened) / 3_600_000));
  if (hours < 48) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

/* ---------- the mapping ---------- */

/** Which rows shout. Two levels, and both are earned rather than decorative.
 *
 * `critical` is for a case that cannot proceed without someone deciding
 * something — a contradiction, a halted automation, an unresolved tenant.
 * `flagged` is for one that is waiting on a person but is otherwise fine.
 */
function toCaseAttention(wire: WireCase, queue: QueueKey): CaseAttention | undefined {
  if (wire.state === 'ConflictDetected' || wire.state === 'Fallback') return 'critical';
  if (queue === 'parked') return 'critical';
  if (wire.stopFlag) return 'flagged';
  if (wire.state === 'ClarificationNeeded' || queue === 'catalog') return 'flagged';
  return undefined;
}

/** Open gaps, or `null` where the question does not apply.
 *
 * `not-evaluated` and "no verdict yet" both mean completeness has not been
 * judged, and `0` would say the opposite — that it was judged and nothing is
 * missing. The column renders a dash for null, which is the truth.
 */
function toGapCount(wire: WireCase, counts: ReadonlyMap<string, number> | undefined): number | null {
  const verdict = wire.completenessVerdict;
  if (!verdict || verdict === 'not-evaluated') return null;
  return counts?.get(wire.id) ?? 0;
}

/**
 * One wire case → one row.
 *
 * `id` is the **case reference**, not the UUID. Every screen uses `record.id` as
 * a display string, a route parameter and a React key at once, and `CLC-104882`
 * in the address bar is worth keeping. The transport holds the reference → UUID
 * index needed to turn a route back into an API call.
 */
export function toCaseRecord(wire: WireCase, context: CaseContext = {}): CaseRecord {
  const queue = toQueue(wire.routingQueue);
  const stateKey = toStateKey(wire.state);
  const customerId = wire.customer?.id ?? null;
  const candidateId = wire.serviceGateCandidate?.id ?? null;
  const attention = toCaseAttention(wire, queue);

  return {
    id: wire.caseReference,
    order: context.orderRefs?.get(wire.id) ?? null,
    /* A name, or nothing — never the id.
     *
     * The id is a uuid. Printing it in a column headed "Customer" gives the
     * reader a string they cannot search a contract by, cannot read aloud and
     * cannot recognise on the second row; and it puts a system identifier on a
     * screen, which this cockpit does not do. The two absences are told apart
     * because they are different facts and lead somewhere different: a case with
     * no customer at all was never anchored, and a case whose customer is not in
     * the index is anchored to a tenant this screen could not name. `tenant`
     * below carries the id, which is what distinguishes them, and
     * `vocabulary.customerName()` has a word for each — here rather than a
     * sentence, because a sentence written at fetch time survives a language
     * switch in React Query's cache. */
    customer: (customerId ? context.customerNames?.get(customerId) : undefined) ?? null,
    // `tenant` is the customer id. The customers screen filters on
    // `record.tenant === customer.id`, and the communication tab resolves a
    // clarification template by it.
    tenant: customerId,
    mriFieldSet: wire.mriFieldSet?.id ?? null,
    // Not on the case row. Which channels a case arrived through lives in
    // intake's `caseItem`, and fetching that per row would be one request per
    // case for a column. The dossier carries it; the queue does without.
    sources: [],
    type: classifyOrderType(wire),
    offering:
      (candidateId ? context.offeringNames?.get(candidateId) : undefined) ?? '—',
    /* Whether a service is cited is the fact; the id may not be showable.
     *
     * The field is absent when nothing is cited and present when something is,
     * and the screens read that distinction rather than the string: the routing
     * tab prints "Not cited" for absent, the rail prints "None — net new". Both
     * are claims about the case, so dropping an unshowable id to absent would
     * turn a true statement into a false one — it would say no service was
     * cited when one was, and only its reference was a uuid. So the field is
     * present and null, and the word for that — the rail's `Referenced` — is
     * written at render by whoever is drawing it.
     *
     * `classifyOrderType()` above reads `wire.existingServiceId` directly and is
     * deliberately left alone: it asks only whether a service is cited, which is
     * true whatever the reference looks like. */
    ...(wire.existingServiceId
      ? { subscription: readable(wire.existingServiceId) ?? null }
      : {}),
    /* The lifecycle, in four fields rather than one sentence.
     *
     * This row is cached by React Query. A status assembled here stays in the
     * language it was fetched in until something refetches it, which is not what
     * a language switch is supposed to mean — so what the row carries is the
     * key, the disposition and the two facts the wording depends on, and
     * `vocabulary.caseState()` / `.caseAction()` do the assembling at render.
     *
     * The disposition still resolves here because it is not a word: an
     * unrecognised state is treated neutrally, and a halted one is treated as
     * blocked whatever state it halted in. */
    statusKey: stateKey,
    statusToken: wire.state,
    disposition: wire.stopFlag ? 'blocked' : stateKey ? caseStates[stateKey].disposition : 'neutral',
    halted: Boolean(wire.stopFlag),
    stopReason: wire.stopReason ?? null,
    // Zero for an unscored case, which is what the API's own `confidenceBelow`
    // filter means by it: a case with no score has not been shown to clear any
    // bar, so it belongs with the low ones rather than above them.
    confidence: wire.confidenceScore ?? 0,
    gaps: toGapCount(wire, context.openGapCounts),
    /* State first, then queue. `AwaitingReply` means the requester has the ball
       wherever the case was routed, and a MACD case waiting on a customer answer
       is not waiting on the MACD desk. Everything else is the desk that owns the
       queue, which `desks` in `taxonomy.ts` says. */
    deskKey: wire.state === 'AwaitingReply' ? 'requester' : desks[queue],
    owner: wire.takenOverBy ?? null,
    age: toAge(wire.createdAt, context.now ?? new Date()),
    queue,
    ...(attention ? { attention } : {}),
    version: String(wire.resourceVersion ?? 0),
  };
}

export function toCaseRecords(
  wires: readonly WireCase[],
  context: CaseContext = {},
): readonly CaseRecord[] {
  // One clock for the whole page: two rows opened a minute apart must not be
  // aged against two different "now"s.
  const shared: CaseContext = { ...context, now: context.now ?? new Date() };
  return wires.map((wire) => toCaseRecord(wire, shared));
}
